import React from 'react';
import { ChevronDown, ChevronUp, Loader2, Layers } from 'lucide-react';
import { Button } from '../ui/button';
import { jobsApi, getErrorMessage } from '../../services/api';
import { toast } from 'sonner';

// Stage-specific accent so each column reads at a glance.
// Mapping stage_id → tailwind 5-step ramp (white → saturated).
const STAGE_RAMPS = {
  sourced: ['bg-white', 'bg-slate-100', 'bg-slate-200', 'bg-slate-300', 'bg-slate-400 text-white', 'bg-slate-500 text-white'],
  screening: ['bg-white', 'bg-sky-50', 'bg-sky-100', 'bg-sky-200', 'bg-sky-300', 'bg-sky-400 text-white'],
  client_submission: ['bg-white', 'bg-violet-50', 'bg-violet-100', 'bg-violet-200', 'bg-violet-300', 'bg-violet-400 text-white'],
  interview_scheduled: ['bg-white', 'bg-amber-50', 'bg-amber-100', 'bg-amber-200', 'bg-amber-300', 'bg-amber-400 text-white'],
  interview_selected: ['bg-white', 'bg-lime-50', 'bg-lime-100', 'bg-lime-200', 'bg-lime-300', 'bg-lime-400 text-white'],
  offer_released: ['bg-white', 'bg-teal-50', 'bg-teal-100', 'bg-teal-200', 'bg-teal-300', 'bg-teal-400 text-white'],
  joined: ['bg-white', 'bg-emerald-50', 'bg-emerald-100', 'bg-emerald-200', 'bg-emerald-300', 'bg-emerald-500 text-white'],
  rejected: ['bg-white', 'bg-red-50', 'bg-red-100', 'bg-red-200', 'bg-red-300', 'bg-red-400 text-white'],
  dropped: ['bg-white', 'bg-orange-50', 'bg-orange-100', 'bg-orange-200', 'bg-orange-300', 'bg-orange-400 text-white'],
  dropped_out: ['bg-white', 'bg-rose-50', 'bg-rose-100', 'bg-rose-200', 'bg-rose-300', 'bg-rose-500 text-white'],
};

const PERIOD_TABS = [
  { key: 'all_time', label: 'All time' },
  { key: 'current_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];

const intensityClass = (value, maxForStage, stageId) => {
  const ramp = STAGE_RAMPS[stageId] || STAGE_RAMPS.sourced;
  if (!value || maxForStage <= 0) return ramp[0];
  const ratio = value / maxForStage;
  const idx = Math.min(ramp.length - 1, Math.max(1, Math.ceil(ratio * (ramp.length - 1))));
  return ramp[idx];
};

/**
 * Client × Pipeline-Stage heat-map.
 *
 * Each row = client, each column = pipeline stage. Cell value = count of
 * applications in that (client, stage) bucket.
 *
 * Optional `onCellClick({clientId, clientName, stage})` for downstream
 * filtering — Pipeline page wires it to scroll/filter the kanban.
 */
const ClientStageHeatmap = ({
  onCellClick,
  // Optional controlled props — when provided, this component renders WITHOUT
  // its own period/date pickers and uses these instead. Lets the parent
  // (Pipeline Analytics tab) drive both heatmap + job-stage table from one
  // shared filter.
  period: controlledPeriod,
  startDate: controlledStart,
  endDate: controlledEnd,
}) => {
  const controlled = controlledPeriod !== undefined;
  const [collapsed, setCollapsed] = React.useState(false);
  const [localPeriod, setLocalPeriod] = React.useState('all_time');
  // Custom date range — used when period === 'custom'. Defaults to current month.
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [localStart, setLocalStart] = React.useState(fmt(monthStart));
  const [localEnd, setLocalEnd] = React.useState(fmt(today));
  const period = controlled ? controlledPeriod : localPeriod;
  const startDate = controlled ? (controlledStart || fmt(monthStart)) : localStart;
  const endDate = controlled ? (controlledEnd || fmt(today)) : localEnd;
  const setPeriod = controlled ? () => {} : setLocalPeriod;
  const setStartDate = controlled ? () => {} : setLocalStart;
  const setEndDate = controlled ? () => {} : setLocalEnd;
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);

  const load = React.useCallback(async (p, range) => {
    setLoading(true);
    try {
      const res = await jobsApi.clientStageMatrix(p, range);
      setData(res.data || null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load matrix'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (collapsed) return;
    if (period === 'custom') {
      if (startDate && endDate && startDate <= endDate) {
        load('custom', { start_date: startDate, end_date: endDate });
      }
    } else {
      load(period, null);
    }
  }, [period, startDate, endDate, collapsed, load]);

  const clients = data?.clients || [];
  const stages = data?.stages || [];
  const matrix = data?.matrix || {};
  const rowTotals = data?.row_totals || {};
  const colTotals = data?.col_totals || {};
  const grand = data?.grand_total || 0;

  // Compute per-stage max for column-relative intensity scaling — a tiny
  // "10 in Joined" cell shouldn't look the same as "10 in Sourced" when
  // Sourced peaks at 500. Column-scaled intensity reads more honestly.
  const maxByStage = React.useMemo(() => {
    const m = {};
    stages.forEach((s) => {
      let mx = 0;
      clients.forEach((c) => {
        const v = matrix[c.id]?.[s.id] || 0;
        if (v > mx) mx = v;
      });
      m[s.id] = mx;
    });
    return m;
  }, [stages, clients, matrix]);

  const isEmpty = !loading && grand === 0;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white"
      data-testid="client-stage-heatmap"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          <Layers className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-slate-800">Client × Pipeline Stage</h3>
          {data ? (
            <span className="text-xs text-slate-500 truncate">
              · {clients.length} clients · {grand} applications
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!collapsed && !controlled && (
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="cs-heatmap-period-tabs">
              {PERIOD_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setPeriod(t.key)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    period === t.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`cs-heatmap-period-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed((v) => !v)}
            className="h-8 px-2"
            data-testid="cs-heatmap-toggle"
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {!collapsed && !controlled && period === 'custom' && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50/60" data-testid="cs-heatmap-custom-range">
          <span className="text-xs font-medium text-slate-600">From</span>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid="cs-heatmap-start-date"
          />
          <span className="text-xs font-medium text-slate-600">to</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid="cs-heatmap-end-date"
          />
          {startDate && endDate && startDate > endDate ? (
            <span className="text-xs text-red-600 font-medium">Start must be ≤ End</span>
          ) : (
            <span className="text-[10px] text-slate-400 italic">
              Tip: select any two dates to scope this analysis
            </span>
          )}
        </div>
      )}

      {!collapsed && (
        <div className="p-3 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading matrix…
            </div>
          ) : isEmpty ? (
            <div className="text-center py-10 text-slate-400 text-sm" data-testid="cs-heatmap-empty">
              No pipeline activity in this period yet. Move candidates through stages to see the breakdown.
            </div>
          ) : (
            <table className="text-xs border-collapse" data-testid="cs-heatmap-table">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 sticky left-0 bg-white z-10 min-w-[180px]">
                    Client
                  </th>
                  {stages.map((s) => (
                    <th
                      key={s.id}
                      className="text-center text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1.5 min-w-[78px]"
                      data-testid={`cs-heatmap-col-${s.id}`}
                      title={s.label}
                    >
                      <div className="truncate max-w-[88px]">{s.label}</div>
                    </th>
                  ))}
                  <th className="text-center text-[10px] uppercase tracking-wide text-slate-700 font-bold px-2 py-1.5 bg-slate-50 min-w-[60px]">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} data-testid={`cs-heatmap-row-${c.id}`}>
                    <td
                      className="text-left text-slate-700 font-medium px-2 py-1.5 sticky left-0 bg-white z-10 truncate max-w-[200px]"
                      title={c.name}
                    >
                      {c.name}
                    </td>
                    {stages.map((s) => {
                      const v = matrix[c.id]?.[s.id] || 0;
                      const rt = rowTotals[c.id] || 0;
                      const isClickable = v > 0 && typeof onCellClick === 'function';
                      const pctOfRow = rt ? ((v / rt) * 100).toFixed(1) : null;
                      const tooltipBase = pctOfRow
                        ? `${c.name} · ${s.label}: ${v} (${pctOfRow}% of pipeline)`
                        : `${c.name} · ${s.label}: ${v}`;
                      return (
                        <td
                          key={`${c.id}-${s.id}`}
                          className={`text-center px-2 py-1.5 border border-slate-100 font-semibold text-slate-800 ${intensityClass(v, maxByStage[s.id] || 0, s.id)} ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-400 transition-shadow' : ''}`}
                          data-testid={`cs-heatmap-cell-${c.id}-${s.id}`}
                          title={isClickable ? `Click to filter · ${tooltipBase}` : tooltipBase}
                          onClick={isClickable ? () => onCellClick({ clientId: c.id, clientName: c.name, stage: s.id, stageLabel: s.label, value: v }) : undefined}
                        >
                          {v || ''}
                        </td>
                      );
                    })}
                    <td
                      className="text-center px-2 py-1.5 font-bold text-slate-900 bg-slate-50"
                      data-testid={`cs-heatmap-row-total-${c.id}`}
                    >
                      {rowTotals[c.id] || 0}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-200 bg-slate-50">
                  <td className="text-left text-slate-700 font-bold px-2 py-1.5 sticky left-0 bg-slate-50 z-10">
                    Total
                  </td>
                  {stages.map((s) => (
                    <td
                      key={`total-${s.id}`}
                      className="text-center px-2 py-1.5 font-bold text-slate-900"
                      data-testid={`cs-heatmap-col-total-${s.id}`}
                      title={`${s.label}: ${colTotals[s.id] || 0} of ${grand}`}
                    >
                      {colTotals[s.id] || 0}
                    </td>
                  ))}
                  <td
                    className="text-center px-2 py-1.5 font-bold text-slate-900"
                    data-testid="cs-heatmap-grand-total"
                  >
                    {grand}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientStageHeatmap;
